import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, ShoppingCartIcon, GiftIcon, BookOpenIcon, TruckIcon, MessageCircleIcon, LockIcon, AwardIcon, ClockIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useCountdown } from '../hooks/useCountdown';
// ─── Checkout URL builder ─────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? template.replace('{{fbclid}}', fbclid) : template;
};
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
let icFiredV5 = false;
const trackV5 = (name: string, price: number) => {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_name: name,
    value: price,
    currency: 'USD'
  });
  if (!icFiredV5) {
    icFiredV5 = true;
    window.fbq('track', 'InitiateCheckout', {
      content_name: name,
      value: price,
      currency: 'USD'
    });
  }
};
// ─── Checkout URLs & Pricing ──────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
type TierKey = 'DIGITAL' | 'PREMIUM' | 'BUNDLE';
interface TierInfo {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  url: string;
  desc: string;
  badge?: string;
}
const TIERS: Record<TierKey, TierInfo> = {
  DIGITAL: {
    id: 'digital',
    name: 'Complete Digital Collection',
    price: 59.97,
    originalPrice: 114.97,
    url: CHECKOUT_DIGITAL,
    desc: '4 Digital Books (Instant Access)'
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium Hardcover Bundle',
    price: 94.97,
    originalPrice: 127.97,
    url: CHECKOUT_PREMIUM,
    desc: 'Digital Collection + Premium Hardcover',
    badge: 'MOST POPULAR'
  },
  BUNDLE: {
    id: 'bundle',
    name: "Time Traveler's Collector Bundle",
    price: 279.97,
    originalPrice: 419.97,
    url: CHECKOUT_BUNDLE,
    desc: 'Complete Digital Collection + Premium Hardcover + 3 Companion Books (Paperback) + Audiobook + Trucker Hat + Framed Print + Tote Bag + Commemorative Mug'
  }
};
const TIER_KEYS: TierKey[] = ['DIGITAL', 'PREMIUM', 'BUNDLE'];
// ─── Images ───────────────────────────────────────────────────────────────────
const GALLERY_IMAGES = ['https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png', 'https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
const LOOK_INSIDE_IMAGES = [
// Book 1: Back In Time
'https://cdn.magicpatterns.com/uploads/tq45Thpd45LDyHLHFRyHb4/image.png', 'https://cdn.magicpatterns.com/uploads/xAZSGuTLPmciaBSXGgJTHj/image.png', 'https://cdn.magicpatterns.com/uploads/g3EfcxHfyFE6HpXeHQ2aZA/image.png',
// Book 2: Behind The Clock Tower
'https://cdn.magicpatterns.com/uploads/rerwYoQ8gs22KNkXTHs5Gf/image.png', 'https://cdn.magicpatterns.com/uploads/rLQyuhnh2qhC64hG3pEah5/image.png', 'https://cdn.magicpatterns.com/uploads/vxi7ma652Y1wW88rpSxwZe/image.png',
// Book 3: Great Scott! 40 Reflections
'https://cdn.magicpatterns.com/uploads/gzWz855tfQDUeagTytQYCe/image.png', 'https://cdn.magicpatterns.com/uploads/3NyPkbzUAwACdE2zMZ8MuJ/image.png', 'https://cdn.magicpatterns.com/uploads/aN6H3njHZtV8jQu2BhuyAk/image.png',
// Book 4: Time. Imagination. Destiny.
'https://cdn.magicpatterns.com/uploads/vRUrFezUsXr4NeCCTTcL6b/image.png', 'https://cdn.magicpatterns.com/uploads/7sMQijfdPvvuxFFNxak5uH/image.png', 'https://cdn.magicpatterns.com/uploads/4LoVuiUpCZ3tw4hi51F4TM/image.png'];
const MINI_REVIEWS = [{
  quote: 'A masterpiece for collectors. If you grew up with Marty and Doc, this is your time machine.',
  name: 'Newman Family',
  loc: 'Boston'
}, {
  quote: 'This book took me straight back to the smell of popcorn and the sound of Huey Lewis.',
  name: 'Kevin R.',
  loc: 'San Diego'
}, {
  quote: 'Reading this felt like watching the trilogy for the first time again.',
  name: 'Laura P.',
  loc: 'Chicago'
}, {
  quote: 'The behind-the-scenes stories made me appreciate just how much heart went into this film.',
  name: 'Brandon T.',
  loc: 'Orlando'
}, {
  quote: "I've collected BTTF merchandise for 20 years. This is the first time I've read something that actually understood why it matters.",
  name: 'Mark T.',
  loc: 'Columbus'
}];
const FULL_REVIEWS = [{
  quote: "I've collected BTTF merchandise for 20 years. Steelbooks, prints, the whole thing. This is the first time I've read something about this film that made me feel like the person who made it actually understood why it matters. Read the whole flagship book in one night.",
  name: 'Mark T., 47',
  loc: 'Columbus'
}, {
  quote: "My son is 13. He'd seen the trilogy twice but didn't really get why I treat it like it's sacred. I read him the section on why fathers show this film to their kids. He went quiet for a second and then said 'that's literally us right now, Dad.'",
  name: 'Sandra R., 52',
  loc: 'Austin'
}, {
  quote: "I went in skeptical. Behind The Clock Tower alone destroyed that pattern. The DeLorean hairdryer story, the Stoltz reshoot, the 18-hour Fox schedule — I've been a fan for 35 years and I didn't know any of it.",
  name: 'David M., 39',
  loc: 'San Diego'
}, {
  quote: "This book took me straight back to the smell of popcorn and the sound of Huey Lewis. It's pure nostalgia.",
  name: 'Kevin R.',
  loc: 'San Diego'
}, {
  quote: 'Reading this felt like watching the trilogy for the first time again. I had tears in my eyes by Chapter Two.',
  name: 'Laura P.',
  loc: 'Chicago'
}, {
  quote: 'The behind-the-scenes stories made me appreciate just how much heart went into this film.',
  name: 'Brandon T.',
  loc: 'Orlando'
}];
const FAQ_ITEMS = [{
  q: 'What formats are the books available in?',
  a: 'The books are available in digital (PDF, instant download) and physical (premium hardcover or paperback, printed on demand). Some bundles include both formats. Digital editions are compatible with all devices — phones, tablets, computers, and e-readers.'
}, {
  q: 'How quickly will I receive my order?',
  a: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical books are professionally printed on demand and typically arrive within 9–14 days. The Time Traveler's Collector Bundle ships in 8–10 business days. Once your order ships, you'll receive a tracking email automatically."
}, {
  q: 'Is this official/licensed content?',
  a: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  q: 'What makes this different from other BTTF books?',
  a: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  q: 'Why only 1,985 copies?',
  a: "1985 is the year Back to the Future was released. The physical edition is limited to exactly 1,985 copies as a tribute to that moment — making each copy a genuine collector's item. Once they're gone, they won't be reprinted."
}, {
  q: 'Can I gift this to someone?',
  a: "Yes. Simply enter the recipient's shipping address at checkout for physical editions. For digital editions, the download link is sent to the email used at checkout — you can forward it to the recipient or purchase using their email address."
}, {
  q: 'Are there future updates or additions?',
  a: 'Digital edition buyers may receive updates if new content is added to the collection. Physical editions are fixed at the time of printing. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  q: 'Who wrote this book?',
  a: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material — it was created intentionally for true fans.'
}, {
  q: 'Is this book generated entirely by AI?',
  a: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  q: 'Is there an audiobook version?',
  a: "Yes. The Back In Time Audiobook Experience is included exclusively in the Time Traveler's Collector Bundle. It's a professionally produced audio version of the main book — perfect for listening on the go."
}, {
  q: 'What if I have an issue with my order?',
  a: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
// Shop Pay installment amounts per tier
const SHOP_PAY_AMOUNTS: Record<TierKey, string> = {
  DIGITAL: '$15',
  PREMIUM: '$23.75',
  BUNDLE: '$70'
};
// ═══ SECTION 1: ANNOUNCEMENT BAR ═══
const AnnouncementBar = () => <div className="bg-[#0f172a] text-white text-center py-2 px-4 text-xs md:text-sm font-medium tracking-wide" data-id="element-1345">
    40th Anniversary Limited Offer — Main Book + 3 Companion Books FREE
  </div>;
// ═══ SECTION 2: HEADER ═══
const HeaderV5 = () => <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm" data-id="element-1346">
    <div className="container mx-auto px-4 py-3 flex justify-between items-center max-w-7xl" data-id="element-1347">
      <div className="font-bold text-xl md:text-2xl tracking-tight text-[#1a1a2e]" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1348">
        Back In Time
      </div>
      <a href="#purchase-panel" className="cta font-semibold text-sm px-4 py-2 rounded transition-colors" style={{
      backgroundColor: '#16a34a',
      color: 'white',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1349">
        Get My Copy
      </a>
    </div>
  </header>;
// ═══ SECTION 3: HERO PRODUCT SECTION ═══
const HeroProductSection = () => {
  const [mainImage, setMainImage] = useState(GALLERY_IMAGES[0]);
  const [selectedTier, setSelectedTier] = useState<TierKey>('DIGITAL');
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  const {
    formatted,
    secondsLeft
  } = useCountdown();
  const currentTier = TIERS[selectedTier];
  const checkoutUrl = buildUrl(currentTier.url);
  const savings = currentTier.originalPrice - currentTier.price;
  const savingsPercent = Math.round(savings / currentTier.originalPrice * 100);
  const hours = String(Math.floor(secondsLeft / 3600)).padStart(2, '0');
  const minutes = String(Math.floor(secondsLeft % 3600 / 60)).padStart(2, '0');
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const handleCta = () => {
    trackV5(currentTier.name, currentTier.price);
  };
  // IntersectionObserver for ClickMagick tracking
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section className="py-4 md:py-8 bg-white" ref={sectionRef} id="purchase-panel" data-id="element-1350">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-1351">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10" data-id="element-1352">
          {/* LEFT: Image Gallery */}
          <div className="w-full lg:w-1/2" data-id="element-1353">
            <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 aspect-square flex items-center justify-center" data-id="element-1354">
              <img src={mainImage} alt="Product View" className="w-full h-full object-contain p-4" fetchpriority="high" width="600" height="600" data-id="element-1355" />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-id="element-1356">
              {GALLERY_IMAGES.map((img, idx) => <button key={idx} onClick={() => setMainImage(img)} className={`flex-shrink-0 w-14 h-14 md:w-18 md:h-18 border-2 rounded overflow-hidden transition-all ${mainImage === img ? 'border-[#1a1a2e]' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-1357">
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="72" height="72" data-id="element-1358" />
                </button>)}
            </div>
            <a href="#look-inside" className="inline-block mt-1 text-sm text-gray-600 underline underline-offset-2 hover:text-[#1a1a2e]" data-id="element-1359">
              Look inside the books
            </a>
          </div>

          {/* RIGHT: Product Info */}
          <div className="w-full lg:w-1/2 flex flex-col" data-id="element-1360">
            {/* Reviews */}
            <div className="flex items-center gap-2 mb-1.5" data-id="element-1361">
              <div className="flex gap-0.5" data-id="element-1362">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-amber-500 fill-amber-500" data-id="element-1363" />)}
              </div>
              <a href="#customers-love-us" className="text-sm text-gray-600 font-medium underline decoration-gray-300 underline-offset-2 hover:text-[#1a1a2e]" data-id="element-1364">
                5,354+ verified reviews
              </a>
            </div>

            {/* Title & Subtitle */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a1a2e] mb-1.5 leading-tight" style={{
            fontFamily: "'Playfair Display', Georgia, serif"
          }} data-id="element-1365">
              Back In Time: 40th Anniversary Collector's Edition
            </h1>
            <p className="text-gray-600 text-sm md:text-base mb-3 leading-relaxed" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1366">
              The most complete legacy document ever assembled for Back to the
              Future. Includes final 40th anniversary content (2025).
            </p>

            {/* Trust Badges — single column on mobile for space optimization */}
            <div className="flex flex-col md:grid md:grid-cols-2 gap-1.5 md:gap-y-2 md:gap-x-3 mb-3 text-sm text-gray-700" data-id="element-1367">
              <div className="flex items-center gap-1.5" data-id="element-1368">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-1369" />{' '}
                Digital + Print Available
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1370">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-1371" />{' '}
                Direct from publisher
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1372">
                <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" data-id="element-1373" />{' '}
                Instant digital delivery
              </div>
            </div>

            {/* Sale Badge & Price */}
            <div className="mb-3" data-id="element-1374">
              <span className="inline-block bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide mb-1.5" data-id="element-1375">
                40th Anniversary Sale
              </span>
              <div className="flex items-baseline gap-2" data-id="element-1376">
                <span className="text-red-600 font-bold text-xl md:text-2xl" data-id="element-1377">
                  {savingsPercent}% OFF
                </span>
                <span className="text-[#1a1a2e] font-bold text-xl md:text-2xl" data-id="element-1378">
                  ${currentTier.price.toFixed(2)}
                </span>
              </div>
              <div className="text-gray-500 text-sm" data-id="element-1379">
                Regular price:{' '}
                <span className="line-through" data-id="element-1380">
                  ${currentTier.originalPrice.toFixed(2)}
                </span>
              </div>
              <div className="mt-1.5" data-id="element-1381">
                <span className="inline-flex items-center gap-1 bg-green-100 border border-green-300 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-1382">
                  You Save ${savings.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Order Bumps */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3" data-id="element-1383">
              <h3 className="font-semibold text-[#1a1a2e] mb-2 text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1384">
                Select your edition:
              </h3>
              <div className="space-y-2" data-id="element-1385">
                {TIER_KEYS.map(key => {
                const tier = TIERS[key];
                const isSelected = selectedTier === key;
                const shopPayAmount = SHOP_PAY_AMOUNTS[key];
                return <label key={key} className={`relative flex items-start p-2.5 border rounded-lg cursor-pointer transition-all ${isSelected ? 'border-green-600 bg-green-50/40 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1386">
                      {tier.badge && <span className="absolute -top-2 right-3 bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-amber-200" data-id="element-1387">
                          {tier.badge}
                        </span>}
                      <div className="flex items-center h-5 mt-0.5" data-id="element-1388">
                        <input type="radio" name="tier" className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500" checked={isSelected} onChange={() => setSelectedTier(key)} data-id="element-1389" />
                      </div>
                      <div className="ml-3 flex-1" data-id="element-1390">
                        <div className="flex justify-between items-start" data-id="element-1391">
                          <span className="font-semibold text-[#1a1a2e] text-sm" data-id="element-1392">
                            {tier.name}
                          </span>
                          <span className="font-bold text-[#1a1a2e] text-sm ml-2" data-id="element-1393">
                            ${tier.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5" data-id="element-1394">
                          {tier.desc}
                        </p>
                        <div className="mt-0.5" data-id="element-1395">
                          <span className="text-green-600 text-[10px] font-bold" data-id="element-1396">
                            You Save $
                            {(tier.originalPrice - tier.price).toFixed(2)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500" data-id="element-1397">
                          <span data-id="element-1398">or 4 payments of</span>
                          <span className="font-bold text-[#5a31f4]" data-id="element-1399">
                            {shopPayAmount}
                          </span>
                          <span data-id="element-1400">with</span>
                          <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-3.5 inline-block" width="48" height="14" loading="lazy" decoding="async" data-id="element-1401" />
                        </div>
                        <p className="text-[10px] text-gray-400" data-id="element-1402">
                          Biweekly interest-free payments
                        </p>
                      </div>
                    </label>;
              })}
              </div>
            </div>

            {/* CTA */}
            <a href={checkoutUrl} onClick={handleCta} className="cta flex items-center justify-center gap-2 w-full text-center font-bold py-3 rounded-lg transition-all text-white text-base" style={{
            backgroundColor: '#16a34a',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1403">
              Get My Collector's Edition
            </a>

            {/* Below CTA: Countdown Timer */}
            <div className="flex items-center justify-center gap-3 mt-2" data-id="element-1404">
              <div className="flex items-center gap-1" data-id="element-1405">
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-1406">
                  {hours}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-1407">
                  hrs
                </span>
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-1408">
                  {minutes}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-1409">
                  min
                </span>
                <div className="bg-gray-800 text-white text-xs font-bold px-1.5 py-1 rounded min-w-[28px] text-center" data-id="element-1410">
                  {secs}
                </div>
                <span className="text-[9px] text-gray-500 uppercase font-semibold" data-id="element-1411">
                  sec
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-1412">
                <ClockIcon className="h-4 w-4" data-id="element-1413" /> lock in savings now
              </div>
            </div>

            {/* Secure Checkout + Payment Icons */}
            <div className="flex items-center justify-center gap-2.5 mt-3" data-id="element-1414">
              <div className="flex items-center gap-1" data-id="element-1415">
                <LockIcon className="h-3.5 w-3.5 text-gray-600" data-id="element-1416" />
                <span className="text-[10px] font-bold uppercase text-gray-600 tracking-wide" data-id="element-1417">
                  Secure Checkout
                </span>
              </div>
              <div className="flex gap-1 items-center" data-id="element-1418">
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5" width="60" height="20" loading="lazy" decoding="async" data-id="element-1419" />
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1420">
                  <rect width="38" height="24" rx="4" fill="#000" data-id="element-1421" />
                  <text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold" fontFamily="system-ui" data-id="element-1422">
                    Pay
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1423">
                  <rect width="38" height="24" rx="4" fill="#fff" stroke="#ddd" data-id="element-1424" />
                  <text x="19" y="15" textAnchor="middle" fill="#5F6368" fontSize="8" fontWeight="bold" fontFamily="system-ui" data-id="element-1425">
                    G Pay
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1426">
                  <rect width="38" height="24" rx="4" fill="#1A1F71" data-id="element-1427" />
                  <text x="19" y="16" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold" fontStyle="italic" fontFamily="system-ui" data-id="element-1428">
                    VISA
                  </text>
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1429">
                  <rect width="38" height="24" rx="4" fill="#fff" stroke="#ddd" data-id="element-1430" />
                  <circle cx="15" cy="12" r="7" fill="#EB001B" data-id="element-1431" />
                  <circle cx="23" cy="12" r="7" fill="#F79E1B" data-id="element-1432" />
                  <path d="M19 7.3a7 7 0 010 9.4 7 7 0 000-9.4z" fill="#FF5F00" data-id="element-1433" />
                </svg>
                <svg className="h-5 w-8" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1434">
                  <rect width="38" height="24" rx="4" fill="#006FCF" data-id="element-1435" />
                  <text x="19" y="16" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="bold" fontFamily="system-ui" data-id="element-1436">
                    AMEX
                  </text>
                </svg>
              </div>
            </div>

            {/* Bundle and save */}
            <button onClick={() => setSelectedTier('BUNDLE')} className="mt-3 w-full py-2 rounded-lg border border-gray-300 bg-[#1a1a2e] text-white text-sm font-semibold hover:bg-[#2a2a3e] transition-colors" data-id="element-1437">
              ⊕ Bundle and save
            </button>
          </div>
        </div>
      </div>
    </section>;
};
// ═══ SECTION 4: SOCIAL PROOF STRIP ═══
const SocialProofStrip = () => {
  const doubledReviews = [...MINI_REVIEWS, ...MINI_REVIEWS];
  return <section className="py-5 md:py-8 bg-gray-50 overflow-hidden" data-id="element-1438">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-1439">
        <h2 className="text-center text-lg md:text-xl font-semibold text-[#1a1a2e] mb-4" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-1440">
          Thousands of happy collectors worldwide
        </h2>
      </div>
      <div className="relative" data-id="element-1441">
        <div className="flex gap-4" style={{
        animation: 'marquee 30s linear infinite',
        width: 'max-content'
      }} data-id="element-1442">
          {doubledReviews.map((r, i) => <div key={i} className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-lg p-3 shadow-sm" data-id="element-1443">
              <div className="flex gap-0.5 mb-1.5" data-id="element-1444">
                {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-3 w-3 text-amber-500 fill-amber-500" data-id="element-1445" />)}
              </div>
              <p className="text-gray-700 text-xs italic leading-relaxed mb-1.5" data-id="element-1446">
                "{r.quote}"
              </p>
              <p className="text-gray-500 text-[10px] font-medium" data-id="element-1447">
                — {r.name}, {r.loc}
              </p>
            </div>)}
        </div>
      </div>
      <style data-id="element-1448">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
};
// ═══ SECTION 5: SPECIFICATIONS + ABOUT THE BOOK ═══
const SpecsAndAbout = () => <section className="py-6 md:py-10 bg-white" data-id="element-1449">
    <div className="container mx-auto px-4 max-w-6xl" data-id="element-1450">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12" data-id="element-1451">
        {/* LEFT: Specifications */}
        <div data-id="element-1452">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-1453">
            Specifications
          </h3>
          <ul className="space-y-1.5 text-sm text-gray-700" data-id="element-1454">
            <li className="flex items-start gap-2" data-id="element-1455">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1456" />
              <span data-id="element-1457">
                <strong data-id="element-1458">Title:</strong> Back In Time: 40th Anniversary
                Collector's Edition (1985-2025)
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1459">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1460" />
              <span data-id="element-1461">
                <strong data-id="element-1462">Volumes:</strong> 4 books included
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1463">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1464" />
              <span data-id="element-1465">
                <strong data-id="element-1466">Format:</strong> Digital (PDF) + Physical editions
                available
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1467">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1468" />
              <span data-id="element-1469">
                <strong data-id="element-1470">Pages:</strong> 435+ pages total
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1471">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1472" />
              <span data-id="element-1473">
                <strong data-id="element-1474">Language:</strong> English
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1475">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1476" />
              <span data-id="element-1477">
                <strong data-id="element-1478">Publisher:</strong> Legendary Reads (The VHS Club)
              </span>
            </li>
            <li className="flex items-start gap-2" data-id="element-1479">
              <CheckIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1480" />
              <span data-id="element-1481">
                <strong data-id="element-1482">Release:</strong> 2025
              </span>
            </li>
          </ul>
        </div>

        {/* RIGHT: About the Book */}
        <div data-id="element-1483">
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2" data-id="element-1484">
            About the Book
          </h3>
          <h4 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-3 leading-tight" style={{
          fontFamily: "'Playfair Display', Georgia, serif"
        }} data-id="element-1485">
            Own this keepsake treasury of 40 years of Back to the Future legacy
          </h4>
          <div className="text-sm text-gray-700 leading-relaxed space-y-2" data-id="element-1486">
            <p data-id="element-1487">
              Since 1985, Back to the Future has been more than a movie — it's a
              cultural landmark that shaped how an entire generation understands
              ambition, risk, and the future itself.
            </p>
            <p data-id="element-1488">
              This collection documents the legacy the way it deserves. Not as
              merchandise. Not as a coffee table book with photos you've seen a
              thousand times. As a cultural archive, assembled the way a
              historian approaches a defining moment: from the inside out.
            </p>
            <p data-id="element-1489">
              <strong data-id="element-1490">Inside you'll discover:</strong> Why Eric Stoltz was fired
              after 6 weeks of filming. The 3 AM breakdown that almost killed
              the production. How a last-minute script change saved the entire
              film. The exact moment Zemeckis knew they'd made history.
            </p>
            <p data-id="element-1491">
              190+ pages of cultural analysis. 87 pages of production secrets.
              87 pages of reflections across 40 years. 71 pages on the
              philosophical legacy of identity, free will, and destiny.
            </p>
            <p className="font-semibold" data-id="element-1492">
              Every page earns its place. Zero fluff. Pure insight.
            </p>
            <p className="text-[#FF6C00] font-semibold" data-id="element-1493">
              Don't miss out on this piece of film history — order today.
            </p>
          </div>
          <a href="#purchase-panel" className="inline-block mt-3 text-sm text-green-600 font-medium underline underline-offset-2 hover:text-green-700" data-id="element-1494">
            Want even more? Check out our collection.
          </a>
        </div>
      </div>
    </div>
  </section>;
// ═══ SECTION 6: GIVING AS A GIFT? ═══
const GiftSection = () => <section className="py-6 md:py-10 bg-gray-50" data-id="element-1495">
    <div className="container mx-auto px-4 max-w-2xl text-center" data-id="element-1496">
      <div className="text-3xl mb-2" data-id="element-1497">🎁</div>
      <h2 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-2" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1498">
        Giving as a gift?
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-1499">
        This collection makes the perfect gift for any Back to the Future fan.
        For physical editions, simply enter the recipient's shipping address at
        checkout. For digital editions, the download link is sent to the email
        used at checkout — you can forward it or purchase using their email.
        Print download and place this certificate in present to your gift
        recipient, it explains their gift is on the way.
      </p>
      <a href="#purchase-panel" className="inline-block text-sm text-green-600 font-semibold underline underline-offset-2 hover:text-green-700" data-id="element-1500">
        Shop gift options →
      </a>
    </div>
  </section>;
// ═══ SECTION 7: LOOK INSIDE ═══
const LookInsideSection = () => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const prev = () => setCurrentIdx(i => i === 0 ? LOOK_INSIDE_IMAGES.length - 1 : i - 1);
  const next = () => setCurrentIdx(i => i === LOOK_INSIDE_IMAGES.length - 1 ? 0 : i + 1);
  return <section id="look-inside" className="py-8 md:py-12 bg-gray-50" data-id="element-1501">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-1502">
        <h2 className="text-center text-2xl md:text-3xl font-bold text-[#1a1a2e] mb-2" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-1503">
          Look Inside
        </h2>
        <p className="text-center text-sm md:text-base text-gray-500 mb-8 max-w-xl mx-auto" data-id="element-1504">
          Preview select pages from inside the books. Note: many pages are
          omitted.
        </p>
        <div className="relative max-w-[420px] mx-auto mt-4 mb-8" data-id="element-1505">
          {/* Book Page Container */}
          <div className="relative bg-white rounded-sm shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border border-gray-200 overflow-hidden" style={{
          aspectRatio: '3/4'
        }} data-id="element-1506">
            {/* Subtle page fold effect (left edge) */}
            <div className="absolute inset-y-0 left-0 w-8 md:w-12 bg-gradient-to-r from-black/[0.06] to-transparent z-10 pointer-events-none" data-id="element-1507"></div>

            <img src={LOOK_INSIDE_IMAGES[currentIdx]} alt={`Page ${currentIdx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-1508" />
          </div>

          {/* Navigation Controls */}
          <button onClick={prev} className="absolute -left-4 md:-left-14 top-1/2 -translate-y-1/2 bg-white text-gray-800 p-2.5 md:p-3 rounded-full shadow-lg border border-gray-100 hover:scale-105 hover:text-[#1a1a2e] transition-all z-20 focus:outline-none focus:ring-2 focus:ring-gray-200" aria-label="Previous page" data-id="element-1509">
            <ChevronLeftIcon className="h-5 w-5 md:h-6 md:w-6" data-id="element-1510" />
          </button>
          <button onClick={next} className="absolute -right-4 md:-right-14 top-1/2 -translate-y-1/2 bg-white text-gray-800 p-2.5 md:p-3 rounded-full shadow-lg border border-gray-100 hover:scale-105 hover:text-[#1a1a2e] transition-all z-20 focus:outline-none focus:ring-2 focus:ring-gray-200" aria-label="Next page" data-id="element-1511">
            <ChevronRightIcon className="h-5 w-5 md:h-6 md:w-6" data-id="element-1512" />
          </button>

          {/* Page Counter */}
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-white text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm border border-gray-200 z-20" data-id="element-1513">
            {currentIdx + 1} / {LOOK_INSIDE_IMAGES.length}
          </div>
        </div>
      </div>
    </section>;
};
// ═══ SECTION 8: THREE-COLUMN INFO ═══
const ThreeColumnInfo = () => <section className="py-6 md:py-10 bg-white" data-id="element-1514">
    <div className="container mx-auto px-4 max-w-6xl" data-id="element-1515">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1516">
        {/* Column 1: FAQ */}
        <div data-id="element-1517">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-1518">
            Frequently Asked Questions
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-1519">
            See FAQs below, or feel free to contact us at
            info.legendaryreads@gmail.com. We're always happy to help!
          </p>
          <div className="space-y-2 text-xs" data-id="element-1520">
            <div data-id="element-1521">
              <strong className="text-[#1a1a2e]" data-id="element-1522">How much is shipping?</strong>
              <p className="text-gray-600 mt-0.5" data-id="element-1523">
                Free shipping on all physical orders.
              </p>
            </div>
          </div>
        </div>

        {/* Column 2: Premium Quality */}
        <div data-id="element-1524">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-1525">
            Premium Quality
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-1526">
            Our presses output the highest quality books to ensure each book
            will be treasured by readers for a lifetime.
          </p>
          <ul className="space-y-1.5 text-xs text-gray-700" data-id="element-1527">
            <li className="flex items-start gap-2" data-id="element-1528">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1529" />
              Exclusively USA printing
            </li>
            <li className="flex items-start gap-2" data-id="element-1530">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1531" />
              Fine art ink printing process for vivid tones
            </li>
            <li className="flex items-start gap-2" data-id="element-1532">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1533" />
              Superior binding
            </li>
            <li className="flex items-start gap-2" data-id="element-1534">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1535" />
              For hardcover books: a 120 pt. case board
            </li>
            <li className="flex items-start gap-2" data-id="element-1536">
              <CheckIcon className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1537" />
              Heavy art paper meant to last
            </li>
          </ul>
        </div>

        {/* Column 3: Unique Gift Idea */}
        <div data-id="element-1538">
          <h3 className="text-sm font-bold uppercase tracking-widest text-[#1a1a2e] mb-3" data-id="element-1539">
            Unique Gift Idea
          </h3>
          <p className="text-xs text-gray-600 mb-3" data-id="element-1540">
            Did you know — many of our customers purchase as gifts?
          </p>
          <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1541">
            You can buy another copy to send as gifts to loved ones.
          </p>
        </div>
      </div>
    </div>
  </section>;
// ═══ SECTION 9: WHO AM I BUYING FROM? ═══
const WhoAmIBuyingFrom = () => <section className="py-6 md:py-10 bg-[#0f172a] text-white" data-id="element-1542">
    <div className="container mx-auto px-4 max-w-5xl text-center" data-id="element-1543">
      <h2 className="text-xl md:text-2xl font-bold mb-3" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1544">
        Who am I buying from?
      </h2>
      <p className="text-sm text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-1545">
        We are Legendary Reads, an independent publisher of premium books and
        collectible products. We've been serving fans for years, which is why
        our customers give us such great reviews. Here are a few ways we provide
        value:
      </p>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-5" data-id="element-1546">
        {[{
        icon: <BookOpenIcon className="h-5 w-5" data-id="element-1547" />,
        label: 'Unique high-quality products'
      }, {
        icon: <StarIcon className="h-5 w-5" data-id="element-1548" />,
        label: '5,354+ verified reviews'
      }, {
        icon: <TruckIcon className="h-5 w-5" data-id="element-1549" />,
        label: 'Fast shipping'
      }, {
        icon: <CheckIcon className="h-5 w-5" data-id="element-1550" />,
        label: 'Satisfaction guarantee'
      }, {
        icon: <MessageCircleIcon className="h-5 w-5" data-id="element-1551" />,
        label: 'Responsive communication'
      }, {
        icon: <LockIcon className="h-5 w-5" data-id="element-1552" />,
        label: 'Secure checkout'
      }].map((item, i) => <div key={i} className="flex flex-col items-center gap-1.5" data-id="element-1553">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white" data-id="element-1554">
              {item.icon}
            </div>
            <span className="text-[10px] text-gray-300 text-center leading-tight" data-id="element-1555">
              {item.label}
            </span>
          </div>)}
      </div>
    </div>
  </section>;
// ═══ SECTION 10: CUSTOMERS LOVE US ═══
const CustomersLoveUs = () => <section id="customers-love-us" className="py-6 md:py-10 bg-white" data-id="element-1556">
    <div className="container mx-auto px-4 max-w-5xl" data-id="element-1557">
      <h2 className="text-center text-xl md:text-2xl font-bold text-[#1a1a2e] mb-6" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1558">
        Customers love us
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1559">
        {FULL_REVIEWS.map((r, i) => <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1560">
            <div className="flex gap-0.5 mb-1.5" data-id="element-1561">
              {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-3.5 w-3.5 text-amber-500 fill-amber-500" data-id="element-1562" />)}
            </div>
            <p className="text-gray-700 text-sm italic leading-relaxed mb-2" data-id="element-1563">
              "{r.quote}"
            </p>
            <div className="flex items-center gap-2" data-id="element-1564">
              <span className="text-[#1a1a2e] text-sm font-semibold" data-id="element-1565">
                — {r.name}
              </span>
              <span className="text-green-600 text-[10px] font-medium flex items-center gap-0.5" data-id="element-1566">
                <CheckIcon className="h-3 w-3" data-id="element-1567" /> Verified Customer
              </span>
            </div>
          </div>)}
      </div>
    </div>
  </section>;
// ═══ SECTION 11: FAQ ═══
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return <section className="py-6 md:py-10 bg-gray-50" data-id="element-1568">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1569">
        <h2 className="text-center text-xl md:text-2xl font-bold text-[#1a1a2e] mb-6" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-1570">
          Questions? We've Got Answers.
        </h2>
        <div className="space-y-1.5" data-id="element-1571">
          {FAQ_ITEMS.map((item, idx) => {
          const isOpen = openIdx === idx;
          return <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-1572">
                <button onClick={() => setOpenIdx(isOpen ? null : idx)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-1573">
                  <span className="font-semibold text-[#1a1a2e] text-sm pr-4" data-id="element-1574">
                    {item.q}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1575" />
                </button>
                {isOpen && <div className="px-4 pb-3 border-t border-gray-100" data-id="element-1576">
                    {item.a.split('\n\n').map((p, i) => <p key={i} className="text-gray-600 text-sm mb-2 last:mb-0 leading-relaxed mt-2" data-id="element-1577">
                        {p}
                      </p>)}
                  </div>}
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// ═══ EXPORT ═══
export const LandingV5 = () => {
  return <div className="w-full min-h-screen bg-white text-gray-900" data-id="element-1578">
      <AnnouncementBar data-id="element-1579" />
      <main data-id="element-1580">
        <HeroProductSection data-id="element-1581" />
        <SocialProofStrip data-id="element-1582" />
        <SpecsAndAbout data-id="element-1583" />
        <GiftSection data-id="element-1584" />
        <LookInsideSection data-id="element-1585" />
        <ThreeColumnInfo data-id="element-1586" />
        <WhoAmIBuyingFrom data-id="element-1587" />
        <CustomersLoveUs data-id="element-1588" />
        <FAQSection data-id="element-1589" />
      </main>
      <Footer data-id="element-1590" />
    </div>;
};